
export type TriaseLevel = "P1" | "P2" | "P3" | "P4" | "DOA";

export interface TriaseSurveyResult {
  level: TriaseLevel | null;
  label: string;
  color: string;
  reasons: string[];
}

export interface TriaseSurveyInput {
  airwayIds: number[];
  pernapasanId: number | null;
  pernapasanTypeId: number | null;
  klinisIds: number[];
  dadaId: number | null;
  nadiIds: number[];
  kulitId: number | null;
  akralId: number | null;
  crtId: number | null;
  kesadaranId: number | null;
  pupilReflekId: number | null;
  reflekCahayaId: number | null;
  eksposurIds: number[];
  isDoa: boolean;
}

const LABELS: Record<TriaseLevel, string> = {
  P1: "P1 — Resusitasi (Merah)",
  P2: "P2 — Emergensi (Kuning)",
  P3: "P3 — Urgensi (Hijau)",
  P4: "P4 — Non-Urgensi (Putih/Hijau)",
  DOA: "DOA — Meninggal (Hitam)"
};

const KATEGORI_ID: Record<TriaseLevel, number> = {
  P1: 1,
  P2: 2,
  P3: 3,
  P4: 4,
  DOA: 0
};

export function triaseLevelToKategoriId(
  level: TriaseLevel | null | undefined
): number | null {
  if (level == null) return null;

  const kategori = KATEGORI_ID[level];

  return kategori == null ? null : kategori;
}

const COLORS: Record<TriaseLevel, string> = {
  P1: "red darken-1",
  P2: "orange darken-2",
  P3: "amber darken-2",
  P4: "green darken-1",
  DOA: "black"
};

function idOf(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === "number") return v;
  if (typeof v === "object" && "id" in (v as Record<string, unknown>)) {
    const id = (v as Record<string, unknown>).id;
    return typeof id === "number" ? id : null;
  }
  return null;
}

function idsOf(v: unknown): number[] {
  if (!Array.isArray(v)) {
    const single = idOf(v);
    return single == null ? [] : [single];
  }
  return v.map(idOf).filter((x): x is number => x != null);
}

export function toTriaseInput(raw: {
  airway?: unknown;
  pernapasan?: unknown;
  pernapasanType?: unknown;
  klinis?: unknown;
  dada?: unknown;
  nadi?: unknown;
  kulit?: unknown;
  akral?: unknown;
  crt?: unknown;
  kesadaran?: unknown;
  pupilReflek?: unknown;
  reflekCahaya?: unknown;
  eksposur?: unknown;
  isDoa?: unknown;
}): TriaseSurveyInput {
  return {
    airwayIds: idsOf(raw.airway),
    pernapasanId: idOf(raw.pernapasan),
    pernapasanTypeId: idOf(raw.pernapasanType),
    klinisIds: idsOf(raw.klinis),
    dadaId: idOf(raw.dada),
    nadiIds: idsOf(raw.nadi),
    kulitId: idOf(raw.kulit),
    akralId: idOf(raw.akral),
    crtId: idOf(raw.crt),
    kesadaranId: idOf(raw.kesadaran),
    pupilReflekId: idOf(raw.pupilReflek),
    reflekCahayaId: idOf(raw.reflekCahaya),
    eksposurIds: idsOf(raw.eksposur),
    isDoa: !!raw.isDoa
  };
}

function hasAnyInput(i: TriaseSurveyInput): boolean {
  return (
    i.airwayIds.length > 0 ||
    i.nadiIds.length > 0 ||
    i.eksposurIds.length > 0 ||
    i.klinisIds.length > 0 ||
    i.pernapasanId != null ||
    i.pernapasanTypeId != null ||
    i.dadaId != null ||
    i.kulitId != null ||
    i.akralId != null ||
    i.crtId != null ||
    i.kesadaranId != null ||
    i.pupilReflekId != null ||
    i.reflekCahayaId != null
  );
}

export function deriveTriaseSurveyPrimer(
  i: TriaseSurveyInput
): TriaseSurveyResult {
  if (i.isDoa) {
    return {
      level: "DOA",
      label: LABELS.DOA,
      color: COLORS.DOA,
      reasons: [
        "Pasien dinyatakan meninggal saat tiba (Dead on Arrival) — tanda vital tidak dinilai."
      ]
    };
  }

  if (!hasAnyInput(i)) {
    return {
      level: null,
      label: "Belum cukup data",
      color: "grey",
      reasons: ["Isi temuan Survey Primer untuk memunculkan saran triase."]
    };
  }

  const p1: string[] = [];
  const p2: string[] = [];
  const p3: string[] = [];

  const shockSigns: string[] = [];
  if (i.akralId === 2) shockSigns.push("akral dingin");
  if (i.crtId === 2) shockSigns.push("CRT > 2 detik");
  if (i.nadiIds.includes(4)) shockSigns.push("nadi lemah");

  // ---------- P1 — Resusitasi ----------
  if (i.kesadaranId === 4)
    p1.push("Tidak responsif (AVPU: Unresponsive).");
  if (i.pernapasanId === 2) p1.push("Napas tidak spontan.");
  if (i.pernapasanTypeId === 2) p1.push("Apnea (pola napas).");
  if (i.airwayIds.includes(5))
    p1.push("Airway: gurgling — sumbatan cairan jalan napas.");
  if (i.airwayIds.includes(2))
    p1.push("Airway: stridor — sumbatan jalan napas atas.");
  if (i.kulitId === 2) p1.push("Sianosis.");
  if (shockSigns.length >= 2)
    p1.push(`Tanda syok (${shockSigns.join(", ")}).`);
  if (i.pupilReflekId === 3)
    p1.push("Pupil midriasis maksimal — kemungkinan herniasi / anoksia berat.");

  // ---------- P2 — Emergensi ----------
  if (i.kesadaranId === 3)
    p2.push("Respons hanya terhadap nyeri (AVPU: Pain).");
  if (i.airwayIds.includes(3)) p2.push("Airway: snoring.");
  if (i.airwayIds.includes(1) || i.airwayIds.includes(4))
    p2.push("Sesak napas / wheezing.");
  if (i.klinisIds.some(id => [1, 2].includes(id)))
    p2.push("Tanda distres napas (retraksi / napas cuping hidung).");
  if (
    i.pernapasanTypeId != null &&
    [3, 4, 5, 6, 7, 8].includes(i.pernapasanTypeId)
  )
    p2.push("Pola napas abnormal.");
  if (shockSigns.length === 1)
    p2.push(`Tanda gangguan sirkulasi (${shockSigns[0]}).`);
  if (i.kulitId === 4 || i.kulitId === 5) p2.push("Pucat / diaforesis.");
  if (i.pupilReflekId === 2) p2.push("Pupil anisokor.");
  if (i.eksposurIds.some(id => [2, 3, 4].includes(id)))
    p2.push("Trauma mayor (penetrasi / luka bakar / perforasi).");

  // ---------- P3 — Urgensi ----------
  if (i.kesadaranId === 2)
    p3.push("Respons terhadap suara (AVPU: Voice).");
  if (i.nadiIds.includes(2)) p3.push("Nadi ireguler.");
  if (i.dadaId === 2) p3.push("Gerakan dada asimetris.");
  if (i.kulitId === 3) p3.push("Jaundice.");
  if (i.eksposurIds.some(id => [1, 5, 6, 7, 8].includes(id)))
    p3.push("Cedera minor (deformitas / jejas / laserasi / krepitasi / abrasi).");

  if (p1.length > 0) {
    return { level: "P1", label: LABELS.P1, color: COLORS.P1, reasons: p1 };
  }
  if (p2.length > 0) {
    return { level: "P2", label: LABELS.P2, color: COLORS.P2, reasons: p2 };
  }
  if (p3.length > 0) {
    return { level: "P3", label: LABELS.P3, color: COLORS.P3, reasons: p3 };
  }

  // ---------- P4 — Non-Urgensi (kondisi stabil) ----------
  const stable: string[] = [];
  if (i.kesadaranId === 1) stable.push("sadar penuh (Alert)");
  if (i.airwayIds.includes(6)) stable.push("airway paten");
  if (i.pernapasanId === 1) stable.push("napas spontan");
  if (i.pernapasanTypeId === 1) stable.push("pola napas normal");
  if (i.akralId === 1) stable.push("akral hangat");
  if (i.crtId === 1) stable.push("CRT < 2 detik");

  if (i.kesadaranId === 1) {
    return {
      level: "P4",
      label: LABELS.P4,
      color: COLORS.P4,
      reasons: [
        stable.length
          ? `Kondisi stabil (${stable.join(", ")}).`
          : "Sadar penuh, tanpa tanda kegawatan."
      ]
    };
  }

  return {
    level: null,
    label: "Belum cukup data",
    color: "grey",
    reasons: [
      "Lengkapi tingkat kesadaran / temuan ABCDE untuk menentukan triase."
    ]
  };
}
